import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar } from '@/components/ui/image';
import { MessageCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  // 从URL参数获取要发送私信的用户ID
  const searchParams = new URLSearchParams(location.search);
  const targetUserId = searchParams.get('to');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  // 如果URL带有to参数，自动创建/选择会话
  useEffect(() => {
    if (targetUserId && user) {
      createOrSelectConversation(targetUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, user]);

  const fetchConversations = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/messages/conversations`, { headers });
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    } finally {
      setLoading(false);
    }
  };

  const createOrSelectConversation = async (userId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/messages/conversations/${userId}`, {}, { headers });
      setSelectedConversation(res.data);
      fetchMessages(res.data.id);
      // 更新URL，移除to参数
      navigate('/messages', { replace: true });
    } catch (err) {
      toast.error(t('messages.cannotCreateConversation', '无法创建会话'));
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.id);
  };

  const fetchMessages = async (conversationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/messages/conversations/${conversationId}/messages`, { headers });
      setMessages(res.data);
      // 更新会话列表中的未读数
      setConversations(conversations.map(c => c.id === conversationId ? { ...c, unread_count: 0 } : c));
      // 滚动到底部
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Failed to fetch messages', err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    setSendingMessage(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const otherUserId = selectedConversation.participants?.find(p => p !== user.id) || selectedConversation.other_user?.id;
      const res = await axios.post(`${API}/messages/send`, {
        receiver_id: otherUserId,
        content: newMessage
      }, { headers });
      setMessages([...messages, res.data]);
      setNewMessage('');
      // 更新会话列表
      fetchConversations();
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(t('messages.sendFailed', '发送失败'));
    } finally {
      setSendingMessage(false);
    }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading', '加载中...')}</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-amber-50/50" data-testid="messages-page">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">{t('messages.title', '私信')}</h1>
        
        <div className="grid md:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <Card className="md:col-span-1 overflow-hidden">
            <CardHeader className="border-b">
              <CardTitle className="text-lg">{t('messages.conversationList', '会话列表')}</CardTitle>
            </CardHeader>
            <ScrollArea className="h-[calc(100%-60px)]">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 text-amber-600">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 text-amber-400" />
                  <p>{t('messages.noConversations', '暂无会话')}</p>
                  <p className="text-sm">{t('messages.startConversation', '在社区中找到感兴趣的用户开始私信吧')}</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-amber-50/50 border-b ${selectedConversation?.id === conv.id ? 'bg-amber-50' : ''}`}
                    onClick={() => selectConversation(conv)}
                  >
                    <Avatar
                      src={conv.other_user?.avatar}
                      name={conv.other_user?.name || '用户'}
                      className="w-12 h-12"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{conv.other_user?.name || t('messages.user', '用户')}</p>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-red-500 text-white">{conv.unread_count}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-amber-600 truncate">{conv.last_message || t('messages.noMessages', '暂无消息')}</p>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </Card>

          {/* Messages Area */}
          <Card className="md:col-span-2 flex flex-col overflow-hidden">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b flex-row items-center gap-3">
                  <Avatar
                    src={selectedConversation.other_user?.avatar}
                    name={selectedConversation.other_user?.name || '用户'}
                    className="w-10 h-10 cursor-pointer"
                    onClick={() => navigate(`/profile/${selectedConversation.other_user?.id}`)}
                  />
                  <div>
                    <CardTitle className="text-lg cursor-pointer hover:text-amber-600" onClick={() => navigate(`/profile/${selectedConversation.other_user?.id}`)}>
                      {selectedConversation.other_user?.name || t('messages.user', '用户')}
                    </CardTitle>
                  </div>
                </CardHeader>
                
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] p-3 rounded-lg ${msg.sender_id === user.id ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800'}`}>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-amber-100' : 'text-amber-500'}`}>
                            {new Date(msg.created_at).toLocaleString('zh-CN')}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                      placeholder={t('messages.inputPlaceholder', '输入消息...')}
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button onClick={sendMessage} disabled={sendingMessage || !newMessage.trim()} className="bg-amber-600 hover:bg-amber-700">
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-amber-600">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-amber-400" />
                  <p>{t('messages.selectConversation', '选择一个会话开始聊天')}</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
